import { AI_LEVELS, Game, type Variant, WHITE, getVariant } from '@chessbut/engine';
import type { CampaignState, Leaderboard } from '@chessbut/shared';
import { ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Board } from '../components/Board.tsx';
import { ChessBut, DayBadge, Rich, variantStyle } from '../components/Brand.tsx';
import { Countdown } from '../components/Countdown.tsx';
import { LeaderboardTable } from '../components/LeaderboardTable.tsx';
import { PlayButtons } from '../components/PlayButtons.tsx';
import { RulesModal } from '../components/Rules.tsx';
import { LocalGame, saveKeyFor } from '../game/localGame.ts';
import { api } from '../lib/api.ts';
import { formatNumber } from '../lib/format.ts';
import { Link, navigate } from '../lib/router.tsx';
import { type PublicStats, canOpenVariant, refreshSession, useSession } from '../lib/session.ts';
import { openAuth } from '../lib/ui.ts';
import { useOnline } from '../online/client.ts';
import { variantUi } from '../variants/ui.ts';

export interface ChallengeInfo {
  available: boolean;
  day: string;
  dayNumber: number;
  variant: string;
  level: number;
  seed: number;
  color: 'white';
  attempt: { result: string; plies: number; durationMs: number | null; attempts: number } | null;
  top: Leaderboard['rows'];
}

export function useChallenge(): ChallengeInfo | null {
  const { me } = useSession();
  const [info, setInfo] = useState<ChallengeInfo | null>(null);
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    api<ChallengeInfo>('/api/challenge')
      .then((r) => !cancelled && setInfo(r))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [me?.id, me?.challenge.done]);
  return info;
}

const NO_MOVES: number[] = [];

/** The variant's starting position with its twist drawn on (snail paths, the goose…). Look, don't touch. */
function HeroBoard({ variant }: { variant: Variant }) {
  const ui = variantUi(variant.id);
  const position = useMemo(() => new Game(variant).position, [variant]);
  const decorations = useMemo(() => ui.decorations?.(position) ?? [], [ui, position]);
  return (
    <div className="hero-board" aria-hidden inert>
      <Board
        position={position}
        orientation={WHITE}
        ui={ui}
        legalMoves={NO_MOVES}
        events={null}
        animKey={0}
        dragged={false}
        lastMove={null}
        checkSquare={-1}
        dangerSquare={-1}
        decorations={decorations}
        showLegalMoves={false}
        showHints
        coordinates
        onMove={() => {}}
      />
    </div>
  );
}

/** A variant's hero: Day badge, headline, the board, the play buttons and the rules in a line. */
export function VariantHero({ variant, campaign, featured }: { variant: Variant; campaign: CampaignState; featured: boolean }) {
  const online = useOnline();
  const [rules, setRules] = useState(false);
  const playing = online.stats?.playing ?? 0;
  return (
    <section className="hero" aria-label={featured ? 'Today’s variant' : variant.info.name}>
      <div className="hero-meta">
        <DayBadge variant={variant} isNew={featured} past={!featured} />
        <span className="variant-name">{variant.info.name}</span>
        {featured ? (
          <span className="clock-note" title="When the daily leaderboard resets (midnight New York)">
            <span className="clock-face">
              <Countdown to={campaign.nextDayAt} onDone={() => setTimeout(refreshSession, 1500)} />
            </span>
            left
          </span>
        ) : (
          playing > 0 && <span className="clock-note">{playing} playing now</span>
        )}
      </div>
      <ChessBut variant={variant} as="h1" />
      <HeroBoard variant={variant} />
      <div className="hero-play">
        <PlayButtons variant={variant} featured={featured} />
      </div>
      <p className="hero-summary">
        <Rich text={variant.info.summary} />{' '}
        <button className="text-link" onClick={() => setRules(true)}>
          All rules
        </button>
      </p>
      {rules && <RulesModal variant={variant} onClose={() => setRules(false)} />}
    </section>
  );
}

function ResumeRows() {
  const { campaign, me } = useSession();
  if (!campaign) return null;
  const games = campaign.released
    .filter((r) => canOpenVariant({ campaign, me }, r.variant))
    .flatMap((r) => (['bot', 'local'] as const).map((mode) => ({ variant: r.variant, mode })))
    .map((g) => ({ ...g, game: LocalGame.restore(saveKeyFor(g)) }))
    .filter((g) => g.game && !g.game.status().over && g.game.game.ply > 0);
  if (!games.length) return null;
  return (
    <div className="list">
      {games.slice(0, 3).map((g) => (
        <button key={`${g.variant}-${g.mode}`} className="list-row as-button" onClick={() => navigate(`/${g.variant}/${g.mode}`)}>
          <span className="grow">
            <span className="name">Resume {getVariant(g.variant)?.info.name}</span>
            <span className="meta">
              {g.mode === 'bot' ? 'vs computer' : 'pass & play'} · {g.game!.game.ply} moves in
            </span>
          </span>
          <ChevronRight className="icon go" aria-hidden />
        </button>
      ))}
    </div>
  );
}

export function ChallengeCard({ info }: { info: ChallengeInfo | null }) {
  if (!info?.available) return null;
  const variant = getVariant(info.variant);
  if (!variant) return null;
  const level = AI_LEVELS.find((l) => l.id === info.level)!;
  const botName = variant.info.bots?.[info.level - 1] ?? level.name;
  const inProgress = LocalGame.restore(saveKeyFor({ variant: info.variant, mode: 'challenge', day: info.day }));
  const started = inProgress && inProgress.game.ply > 0 && !inProgress.status().over;
  const a = info.attempt;
  return (
    <section className="challenge-card">
      <div className="grow">
        <div className="challenge-title">Daily challenge</div>
        {a ? (
          <div className="muted small">
            {a.result === 'win' ? `Won in ${Math.ceil(a.plies / 2)} moves ✓` : a.result === 'draw' ? 'Drew ✓' : 'Lost. Practice is open.'} Everyone faced{' '}
            {botName}.
          </div>
        ) : (
          <div className="muted small">
            Beat {botName}. First try counts. <b>+20 pts</b>
          </div>
        )}
      </div>
      <Link to="/challenge" className="btn small">
        {a ? 'See' : started ? 'Back' : 'Go'}
      </Link>
    </section>
  );
}

function StatStrip() {
  const { me } = useSession();
  if (!me) return null;
  const overall = me.ratings.overall;
  return (
    <section className="stats">
      <div className="stat">
        <div className="k">Rating</div>
        <div className="v">
          {overall ? overall.rating : 1500}
          {(!overall || overall.provisional) && <span className="q">?</span>}
        </div>
      </div>
      <div className="stat">
        <div className="k">Today</div>
        <div className="v">
          {me.today.rank ? `#${me.today.rank}` : me.today.points}
          {!me.today.rank && <small>pts</small>}
        </div>
      </div>
      <div className="stat">
        <div className="k">Best streak</div>
        <div className="v">
          {me.streak.best}
          <small>{me.streak.best === 1 ? 'day' : 'days'}</small>
        </div>
      </div>
    </section>
  );
}

function TodayTop() {
  const { me } = useSession();
  const [board, setBoard] = useState<Leaderboard | null>(null);
  useEffect(() => {
    api<Leaderboard>('/api/leaderboard?kind=daily')
      .then(setBoard)
      .catch(() => {});
  }, [me?.today.points]);
  return (
    <section className="group">
      <div className="spread">
        <h2 className="section-title">Today’s top players</h2>
        <Link to="/leaderboard" className="see-all">
          See all
        </Link>
      </div>
      <LeaderboardTable
        rows={board?.rows.slice(0, 5) ?? []}
        me={board?.me ?? null}
        unit="pts"
        meId={me?.id ?? null}
        empty="Nobody has scored today. Win a rated game and the top spot is yours."
      />
      {me?.guest && me.today.points > 0 && (
        <button
          className="btn strong block"
          onClick={() => openAuth('signup', `You have ${me.today.points} points today${me.today.rank ? `: that’s #${me.today.rank}` : ''}. Create an account to get on the board.`)}
        >
          Claim your spot{me.today.rank ? ` (#${me.today.rank})` : ''}
        </button>
      )}
    </section>
  );
}

export function ArchiveList({ campaign, exclude, limit }: { campaign: CampaignState; exclude?: string | null; limit?: number }) {
  const { me } = useSession();
  const items = campaign.released.filter((r) => r.variant !== exclude && canOpenVariant({ campaign, me }, r.variant));
  if (!items.length) return null;
  return (
    <div className="list archive">
      {items.slice(0, limit ?? items.length).map((r) => {
        const v = getVariant(r.variant);
        if (!v) return null;
        const ui = variantUi(v.id);
        return (
          <Link key={r.variant} to={`/${v.id}`} className="list-row" style={variantStyle(v)}>
            <DayBadge day={r.day} past={r.variant !== campaign.featured?.variant} isNew={r.variant === campaign.featured?.variant} />
            <span className="grow">
              <span className="name">chess, but {v.info.but}</span>
              <span className="meta">
                {v.info.name}
                {r.day === 0 ? ' · the original' : r.releasedOn ? ` · ${r.releasedOn}` : ''}
                {r.draft ? ' · draft' : ''}
              </span>
            </span>
            {ui.heroImage ? <img className="row-art" src={ui.heroImage} alt="" /> : <span className="row-art">{v.info.emoji}</span>}
          </Link>
        );
      })}
    </div>
  );
}

export function StatsTicker({ stats }: { stats: PublicStats | null }) {
  if (!stats || stats.games < 1) return null;
  return (
    <Link to="/stats" className="ticker">
      <span>
        <b className="num">{formatNumber(stats.games)}</b> {stats.games === 1 ? 'game' : 'games'} played
      </span>
      <span>
        <b className="num">{formatNumber(stats.players)}</b> {stats.players === 1 ? 'player' : 'players'}
      </span>
      {stats.countries > 1 && (
        <span>
          <b className="num">{stats.countries}</b> countries
        </span>
      )}
    </Link>
  );
}

export function Home() {
  const { campaign, stats } = useSession();
  const challenge = useChallenge();
  if (!campaign) return <div className="page loading"><span className="spinner big" /></div>;
  const featured = campaign.featured ? getVariant(campaign.featured.variant) : null;
  return (
    <div className="page home" style={variantStyle(featured)}>
      {featured ? (
        <VariantHero variant={featured} campaign={campaign} featured />
      ) : (
        <section className="today">
          <span className="day-badge">Day 1 soon</span>
          <ChessBut as="h1" twist="different every day" />
          <p className="muted">
            Day 1 drops in <Countdown to={campaign.nextDayAt} onDone={() => setTimeout(refreshSession, 1500)} />. Warm up with classic chess below.
          </p>
        </section>
      )}
      <div className="home-grid">
        <div>
          <ResumeRows />
          <ChallengeCard info={challenge} />
          <StatStrip />
          <TodayTop />
        </div>
        <div>
          <section className="group">
            <div className="spread">
              <h2 className="section-title">Archive</h2>
              <Link to="/days" className="see-all">
                See all
              </Link>
            </div>
            <ArchiveList campaign={campaign} exclude={featured?.id} limit={4} />
          </section>
          <StatsTicker stats={stats} />
        </div>
      </div>
    </div>
  );
}
